import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem,
  Label,
  FieldError
} from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FieldYearSelectProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  path: Path<T>;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  className?: string;
  /** Earliest selectable year, inclusive. Defaults to the current year - 100. */
  min?: number;
  /** Latest selectable year, inclusive. Defaults to the current year + 10. */
  max?: number;
  [key: string]: unknown;
};

/**
 * A year picker rendered as a select whose options are laid out as a compact
 * year grid. It shares the field chrome (trigger, rotating indicator, clear,
 * keyboard nav, placeholder) with the other selects; it is deliberately close
 * to {@link FieldSelect} but owns the year range and grid layout so those
 * concerns stay out of the generic select. Distinct from the numeric year input
 * `FieldYear`.
 */
export const FieldYearSelectBase = <T extends FieldValues = FieldValues>({
  label,
  placeholder,
  isRequired,
  className,
  min,
  max,
  ...props
}: FieldYearSelectProps<T>) => {
  const { t } = useTranslation();
  const { path } = props;
  const {
    field: { disabled, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path
  });

  const value = useMemo(
    () => (fieldValue == null || fieldValue === '' ? null : String(fieldValue)),
    [fieldValue]
  );

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const maxYear = max ?? currentYear + 10;
    const minYear = min ?? currentYear - 100;
    const count = Math.max(0, maxYear - minYear + 1);
    // Descending so recent years are first (less scrolling for common cases).
    return Array.from({ length: count }, (_, i) => maxYear - i);
  }, [min, max]);

  return (
    <Select
      {...fieldProps}
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      placeholder={placeholder ?? t('rhino.frontend.messages.select_a_year')}
      value={value ?? null}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <SelectTrigger>
        <SelectValue />
        <SelectIndicator />
      </SelectTrigger>
      <SelectPopover>
        <ListBox
          layout="grid"
          className="grid max-h-64 grid-cols-4 gap-1 overflow-y-auto p-1"
        >
          {years.map((y) => (
            <ListBoxItem key={y} id={String(y)} textValue={String(y)}>
              {y}
            </ListBoxItem>
          ))}
        </ListBox>
      </SelectPopover>
      <FieldError>{error?.message}</FieldError>
    </Select>
  );
};

export const FieldYearSelect = (props: FieldYearSelectProps) =>
  useGlobalComponent('FieldYearSelect', FieldYearSelectBase, props);
